/**
 * Every fact and every line of copy on the site lives here.
 *
 * NOTE FOR DANIEL: `site.since` is the only number I derived rather than
 * confirmed. Your previous site said "over eight years" around 2018. Change it
 * here and it updates everywhere it appears.
 */

export const site = {
  name: "Daniel Larkin",
  role: "Systems Architect & Technical Founder",
  email: "daniel@larkin.nz",
  origin: "Aotearoa New Zealand",
  location: "Indonesia · New Zealand · UK",
  /** The same fact as a sentence, for prose and for the markdown renderings. */
  based: "UTC+8, between Indonesia and New Zealand, with summers in the UK",
  timezone: "UTC+8",
  since: 2010,
  url: "https://daniel.larkin.nz",
  title: "Daniel Larkin · Systems Architect & Technical Founder",
  description:
    "Systems architect and technical founder. Applied AI, distributed systems, Rust and embedded, and edge infrastructure. Founder of DiveLogic, OneIsland and Bloom. On Cloudflare Workers since 2017. Available globally.",
  github: "https://github.com/danzlarkin",
  medium: "https://medium.com/@danzlarkin",
} as const;

export const hero = {
  headline: {
    pre: "I build systems for places where failure ",
    em: "isn’t recoverable.",
  },
  lead: "Systems architect and technical founder. Applied AI, distributed systems, and edge infrastructure down to the firmware it runs against. Usually a few years before it was comfortable.",
  readouts: [
    { k: "Based", v: "UTC+8 · works anywhere" },
    { k: "Building since", v: String(site.since) },
    { k: "On Workers since", v: "2017" },
  ],
} as const;

export const thesis = {
  act: "01",
  marker: "Thesis",
  heading: "Three industries, one failure mode.",
  body: [
    "Vertical farms, off-grid houses and technical divers look like unrelated industries. They are the same engineering problem.",
    "Each is a closed loop running under constraint, where a model is the only thing standing between the system and a failure you cannot undo. A hydroponic bay drifts and a crop is gone in days. A house misjudges its power budget in winter and the people inside are cold. A decompression schedule is wrong by a few minutes and a diver is hurt on the way up.",
    "I build the instrumentation for those loops: the sensing, the modelling, the control, and the distributed infrastructure that keeps them honest when the network is not.",
  ],
  pullquote:
    "Most systems are not life-critical. The ones worth hiring me for behave as if they are.",
} as const;

export type Venture = {
  act: string;
  marker: string;
  id: string;
  name: string;
  years: string;
  domain: string;
  tagline: string;
  body: string[];
  specs: { k: string; v: string }[];
  link?: { href: string; label: string };
};

export const ventures: Venture[] = [
  {
    act: "02",
    marker: "Bloom",
    id: "bloom",
    name: "Bloom",
    years: "2016–2017",
    domain: "Vertical farming · computer vision",
    tagline: "Teaching a camera to notice a plant is sick before a grower can.",
    body: [
      "Bloom was a vertical-farming stack built around one question: can a machine see disease earlier than a person walking the aisles?",
      "We trained convolutional image models on leaf imagery to classify disease and deficiency from the visual signal alone, then fed the classification straight back into the hydroponic control loop. Nutrient dosing, EC and pH targets, photoperiod and spectrum all moved on the model, so the response was automatic rather than advisory.",
      "This was 2016. There was no off-the-shelf vision API worth the name for this, no managed training platform that made sense at our budget, and no pretrained agricultural model to fine-tune. We gathered the dataset, labelled it, trained it, and ran inference on hardware sitting in a humid room next to the plants.",
    ],
    specs: [
      { k: "Domain", v: "Controlled-environment agriculture" },
      {
        k: "Models",
        v: "Convolutional image classification · disease & deficiency",
      },
      { k: "Control", v: "Closed-loop dosing, EC / pH, photoperiod, spectrum" },
      { k: "Constraint", v: "On-site inference, intermittent connectivity" },
    ],
  },
  {
    act: "03",
    marker: "OneIsland",
    id: "oneisland",
    name: "OneIsland",
    years: "2018–2022",
    domain: "CAD / CAM · robotics · off-grid housing",
    tagline: "A browser that outputs a house.",
    body: [
      "OneIsland compressed the distance between designing an off-grid dwelling and manufacturing one.",
      "The core was a web-based parametric CAD environment: geometry kernel, constraint solving and real-time visualisation running in the browser, coupled directly to CAM output and robotic fabrication. A change to the design propagated through to toolpaths and cut files without anyone redrawing anything.",
      "The hard part was never the rendering. It was building a geometry system that stayed numerically stable across thousands of parametric edits, that emitted toolpaths a machine would actually accept, and that treated the physical constraints of living off-grid as first-class parameters rather than notes in a PDF: thermal envelope, water capture, power budget, structure.",
    ],
    specs: [
      { k: "Domain", v: "Off-grid housing, design-to-manufacture" },
      {
        k: "Core",
        v: "Browser-native parametric CAD, geometry kernel, constraint solver",
      },
      { k: "Output", v: "CAM toolpaths, robotic fabrication, cut files" },
      {
        k: "Encoded",
        v: "Thermal envelope, water capture, power budget, structure",
      },
    ],
    link: { href: "https://oneis.land", label: "oneis.land" },
  },
  {
    act: "04",
    marker: "DiveLogic",
    id: "divelogic",
    name: "DiveLogic",
    years: "Present",
    domain: "Decompression modelling · applied AI",
    tagline: "Right to the minute, or not right at all.",
    body: [
      "DiveLogic is decompression planning for technical, cave and closed-circuit divers. It is the segment where the plan is the safety equipment.",
      "It implements Bühlmann ZHL-16C with gradient factors and VPM-B, tracks CNS, OTU and IBCD across every phase of a dive, and plans multi-gas trimix and heliox profiles with automatic switch depths, bailout ascents, lost-gas contingencies and rule-of-thirds reserves. It syncs over Bluetooth with Shearwater Perdix, Petrel and Teric computers, and replays tissue loading afterwards so a team can debrief against what actually happened.",
      "The AI sits on top of the physiology, never in place of it. Divers interrogate a plan in natural language and the system proposes adjustments, but every number it returns is produced by the deterministic model underneath and traceable back to it. In this domain a plausible answer is worse than no answer.",
    ],
    specs: [
      { k: "Models", v: "Bühlmann ZHL-16C · gradient factors · VPM-B" },
      { k: "Tracking", v: "CNS · OTU · IBCD, across all phases" },
      { k: "Gases", v: "Nitrox · trimix · heliox, automatic switch depths" },
      { k: "Hardware", v: "Shearwater Perdix / Petrel / Teric over Bluetooth" },
    ],
    link: { href: "https://divelogic.ai", label: "divelogic.ai" },
  },
];

export const edge = {
  act: "05",
  marker: "Edge",
  heading: "At the edge since there was an edge.",
  body: [
    "Cloudflare shipped Workers in 2017. I was building on it that year, when the runtime had no KV, no Durable Objects, no queues, no D1, and a deployment story you assembled yourself. I shipped tooling for the platform because the platform did not have any yet.",
    "That period taught the most useful thing I know about distributed systems: when compute is everywhere and state is nowhere, correctness stops being a database feature and becomes a design problem. Everything since is the same problem with better primitives: edge inference, globally consistent state, coordination without a coordinator.",
    "The same instinct took me to Rust. Where a wrong answer has a physical consequence I want the compiler arguing with me before the system does, and I want to know what the machine is actually doing with my memory and my microseconds. Rust also compiles to WebAssembly for the edge and to bare metal for a microcontroller, so one implementation of a model can run in a browser, in a Worker, and on the device holding the sensor, and return the same number in all three. That property is worth more than any framework.",
    "Below all of that is hardware. Inference running next to the plants at Bloom, toolpaths driving machines at OneIsland, dive computers syncing over Bluetooth at DiveLogic: every venture I have built ended at a device standing in a place with unreliable power and worse network. I write the firmware and the control loops that sit on it, because that is where the interesting failures live. Not in the dashboard.",
  ],
  specs: [
    { k: "Edge", v: "Cloudflare Workers · Durable Objects · WebAssembly" },
    { k: "Systems", v: "Rust, and C where the hardware insists" },
    {
      k: "Targets",
      v: "Microcontrollers · single-board Linux · the edge · the browser",
    },
    {
      k: "Constraint",
      v: "Intermittent power, intermittent network, no second attempt",
    },
  ],
} as const;

export const practice = {
  act: "06",
  marker: "Practice",
  heading: "Three ways to bring me in.",
  engagements: [
    {
      n: "01",
      title: "Architecture & advisory",
      hook: "A hard system, and a decision you cannot unmake.",
      body: "I take the architecture apart with your team and give you back a written design, the failure modes it survives, the ones it does not, and the trade you are actually making. Ends in a document you can hand to an engineer or a board.",
      shape: "2–6 weeks",
    },
    {
      n: "02",
      title: "Build it with you",
      hook: "Hands on the part nobody wants to own.",
      body: "The model, the distributed core, the edge layer, the real-time control loop: whichever piece your team cannot de-risk alone. I build it alongside them and leave the knowledge behind, rather than a dependency on me.",
      shape: "3–6 months",
    },
    {
      n: "03",
      title: "Technical due diligence",
      hook: "Whether the claim survives contact.",
      body: "An assessment of an architecture, a team or an AI claim, written for investors and acquirers. Specific, evidenced, and unflattering where it needs to be. I will tell you what I would need to be true, and whether it is.",
      shape: "1–2 weeks",
    },
  ],
  domains: [
    "Applied AI & ML systems",
    "Distributed systems & consistency",
    "Edge architecture & Cloudflare Workers",
    "Rust & WebAssembly",
    "Embedded firmware & real-time control",
    "Sensing & instrumentation",
    "Design-to-manufacture pipelines",
    "Systems where safety is a requirement, not a feature",
  ],
} as const;

export const record = {
  marker: "Record",
  heading: "Before all of that.",
  lead: "Before the ventures I ran Tecto, my own systems architecture consultancy in New Zealand, delivering for institutional clients: the University of Otago, the New Zealand Parliament and ACC. Systems with real users, real auditors, and no appetite for surprises.",
  items: [
    { v: "30,000+", k: "customers on products I have built and shipped." },
    { v: "5,000+", k: "users on open-source libraries I maintain." },
    {
      v: "2 × mentor",
      k: "Startup Weekend, plus judging entrepreneurship programmes across New Zealand.",
    },
  ],
} as const;

export const human = {
  act: "07",
  marker: "Human",
  heading: "Available globally.",
  body: [
    "New Zealand originally. I work from UTC+8 now, between Indonesia and New Zealand, with summers in the UK, and to whatever timezone or site an engagement actually needs. Distributed work is not a concession I make. It is the condition I have designed for from the start.",
    "The rest of the time I am away from a desk: off-grid, up mountains, or a long way underwater. That is not a personality section. It is where the work comes from. Every system I have built started as a problem I met somewhere without reliable power, without reliable network, and without a second attempt.",
    "The best engineering I know is the kind that assumes conditions will get worse.",
  ],
  photoAlt:
    "Daniel Larkin, smiling, in dense New Zealand rainforest wearing a bright floral shirt.",
} as const;

export const contact = {
  act: "08",
  marker: "Contact",
  heading: "Tell me what’s hard.",
  lead: "If you have a system that has to hold, and you would rather find out now than at 3am, write to me. I read everything and reply to anything specific.",
  next: [
    {
      k: "A reply, from me",
      v: "Not a form response and not an assistant. Usually within two working days, wherever you are.",
    },
    {
      k: "A call, if it fits",
      v: "Forty-five minutes on the actual system. You will get an opinion on that call whether or not we work together.",
    },
    {
      k: "A written proposal",
      v: "Scope, shape, price and what I would need from your team. No retainer talk before we know the problem.",
    },
  ],
  fields: {
    name: { label: "Your name", placeholder: "Ada Lovelace" },
    email: { label: "Email I can reply to", placeholder: "you@company.com" },
    company: { label: "Company", placeholder: "Acme Robotics", optional: true },
    message: {
      label: "What are you working on?",
      placeholder:
        "The system, the constraint, and what happens if it fails. Detail helps. I reply to specifics.",
    },
  },
  submit: "Send it",
  sending: "Sending…",
  success: "Sent. I'll send you a response as soon as possible.",
} as const;

/** Section → WebGL act index. Drives the graph morph. */
export const ACTS = {
  hero: 0,
  thesis: 0,
  bloom: 1,
  oneisland: 2,
  divelogic: 3,
  edge: 4,
  practice: 5,
  record: 5,
  human: 5,
  contact: 5,
} as const;
